'use client';

import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { useEffect, useRef } from 'react';

import type { ChartType, ColumnMapping } from '@/types/dashboard';

interface ColumnMappingBarProps {
  columns: string[];
  data: Record<string, unknown>[];
  columnMapping: ColumnMapping;
  chartType: ChartType;
  onChange: (mapping: ColumnMapping) => void;
}

export function ColumnMappingBar({
  columns,
  data,
  columnMapping,
  chartType,
  onChange,
}: ColumnMappingBarProps) {
  const prevColumnsRef = useRef<string>('');
  const prevPieModeRef = useRef<boolean | null>(null);

  // Auto-detect when columns change
  useEffect(() => {
    if (!columns.length || !data.length) return;
    const columnsKey = columns.join(',');
    if (columnsKey === prevColumnsRef.current) return;
    prevColumnsRef.current = columnsKey;

    // Detect numeric columns from the data
    const numericCols = columns.filter((col) =>
      data.some((row) => typeof row[col] === 'number')
    );
    const nonNumericCols = columns.filter((col) => !numericCols.includes(col));

    const autoMapping: ColumnMapping = {};
    if (isPieType(chartType)) {
      autoMapping.labelColumn = nonNumericCols[0] ?? columns[0];
      autoMapping.valueColumn = numericCols[0] ?? columns[1] ?? columns[0];
    } else {
      autoMapping.xColumn = nonNumericCols[0] ?? columns[0];
      autoMapping.yColumns =
        numericCols.length > 0
          ? [numericCols[0]]
          : columns.length > 1
            ? [columns[1]]
            : [];
    }
    onChange(autoMapping);
  }, [columns, data, chartType, columnMapping, onChange]);

  // Re-map when switching between pie and non-pie chart types
  useEffect(() => {
    if (!columns.length) return;
    const currentPieMode = isPieType(chartType);
    if (
      prevPieModeRef.current !== null &&
      currentPieMode !== prevPieModeRef.current
    ) {
      if (currentPieMode) {
        // Switching to pie: translate xColumn/yColumns → labelColumn/valueColumn
        onChange({
          labelColumn: columnMapping.xColumn ?? columns[0],
          valueColumn: columnMapping.yColumns?.[0] ?? columns[1] ?? columns[0],
        });
      } else {
        // Switching from pie: translate labelColumn/valueColumn → xColumn/yColumns
        onChange({
          xColumn: columnMapping.labelColumn ?? columns[0],
          yColumns: [columnMapping.valueColumn ?? columns[1] ?? columns[0]],
        });
      }
    }
    prevPieModeRef.current = currentPieMode;
  }, [chartType]); // eslint-disable-line react-hooks/exhaustive-deps

  const numericCols = columns.filter((col) =>
    data.some((row) => typeof row[col] === 'number')
  );

  if (chartType === 'TABLE') {
    return null;
  }

  if (isPieType(chartType)) {
    return (
      <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
        <ColumnSelect
          label='Label'
          value={columnMapping.labelColumn ?? ''}
          columns={columns}
          onChange={(labelColumn) =>
            onChange({ ...columnMapping, labelColumn })
          }
        />
        <ColumnSelect
          label='Value'
          value={columnMapping.valueColumn ?? ''}
          columns={numericCols}
          onChange={(valueColumn) =>
            onChange({ ...columnMapping, valueColumn })
          }
        />
      </Stack>
    );
  }

  const yColumns = columnMapping.yColumns ?? [];

  return (
    <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
      <ColumnSelect
        label='X Axis'
        value={columnMapping.xColumn ?? ''}
        columns={columns}
        onChange={(xColumn) => onChange({ ...columnMapping, xColumn })}
      />
      {yColumns.map((yCol, i) => (
        <ColumnSelect
          key={i}
          label={yColumns.length > 1 ? `Y Axis ${i + 1}` : 'Y Axis'}
          value={yCol}
          columns={numericCols}
          onChange={(val) => {
            const newY = [...yColumns];
            newY[i] = val;
            onChange({ ...columnMapping, yColumns: newY });
          }}
        />
      ))}
      {chartType !== 'SCATTER' && numericCols.length > yColumns.length && (
        <IconButton
          size='small'
          onClick={() =>
            onChange({
              ...columnMapping,
              yColumns: [
                ...yColumns,
                numericCols.find((c) => !yColumns.includes(c)) ?? '',
              ],
            })
          }
          title='Add Y series'
        >
          +
        </IconButton>
      )}
    </Stack>
  );
}

function isPieType(chartType: ChartType): boolean {
  return chartType === 'PIE' || chartType === 'DONUT';
}

function ColumnSelect({
  label,
  value,
  columns,
  onChange,
}: {
  label: string;
  value: string;
  columns: string[];
  onChange: (value: string) => void;
}) {
  return (
    <FormControl size='small' sx={{ minWidth: 120 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {columns.map((col) => (
          <MenuItem key={col} value={col}>
            {col}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
